// EndpointBuilder component
// interactive endpoint builder based on material-ui chips - see 'Channels' tab on the platform, under 'Endpoint Builder' for an example of how it looks
//
// -------------------------- USAGE EXAMPLE --------------------------
//
//   |  var service = new EndpointBuilderService()                                                         # create the service used by this component
//   |
//   |  var structure: IEndpointBuilderRoot = {                                                            # create the structure of the component | title -> subtitle -> chips
//   |    defaults: [],                                                                                    # 'defaults' can be used for default endpoint parameters always present
//   |    titles: [
//   |        {
//   |          title: 'I am a Title!',
//   |          subtitles: [
//   |            {
//   |              subtitle: 'I am a SubTitle',
//   |              description: 'Blablabla',
//   |              isVisible: () => ( currentWeather() === 'sunny'),                                      # 'isVisible' can be used to trigger node visibility at the runtime
//   |              chips: [
//   |                {
//   |                  text: 'I am a clickable chip!',
//   |                  parameterName: 'test',                                                             # name of the GET variable for this chip
//   |                  parameterValue: 'hello_world',                                                     # value of the GET variable for this chip, can also be computed at runtime with 'makeParameterValue'
//   |                  isVisible: () => (service.getChip('TITLE', 'SUBTITLE', 'CHIPTEXT').selected)       # getChip can be used to access chips in a service
//   |                }
//   |              ]
//   |            }
//   |          ]
//   |        }
//   |      ]
//   |    }
//   |
//   |  service.setStructure(structure)                                                                    # associate the structure to this endpoint-builder service
//   |  service.setEndpoint('http://getpublica.com/aroute')                                                # set the base endpoint (GET parameters will be added to it)
//   |  service.setServiceRefreshHandler(() => {                                                           # this callback is used when the service needs to refresh the child component because of updates in it
//   |    this.setState({ endpointBuilderService: this.state.endpointBuilderService })                     # use something here to reload component, state can be used for that
//   |  })
//   |  service.endpointBuilderService.setEndpointChangeHandler((newEndpoint: string) => {                 # this callback is used when the service have refreshed the endpoint
//   |    this.setState({ EndpointURL: newEndpoint })
//   |  })
//   |
//   |  <EndpointBuilder service=service />                                                                # then, just instantiate the component like so :)
//
// ------------- real life example in site-settings-list-view.tsx -------------

import { Chip, Grid, Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import React from 'react';

import { getColorTheme, getSSAIWiki } from '~/src/dash/resellers/utils/importResellerConfig';
import { IWithStylesProps } from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import {
  EndpointBuilderService,
  IEndpointBuilderChip,
  IEndpointBuilderSubtitle,
  IEndpointBuilderTitle,
} from '~/src/dash/utils/endpointBuilderService';

export interface IProps {
  service: EndpointBuilderService; // service for the endpoint builder - define structure + provide chip click handler function
}

export const EndpointBuilder = decorate(
  {
    styles: () => ({
      chip: {
        '&.MuiChip-colorPrimary': {
          backgroundColor: getColorTheme('chipSelectActiveBackground') + ' !important',
        },
      },
    }),
  },
  class EndpointBuilder extends React.Component<IProps & IWithStylesProps> {
    // render titles
    render = () => (
      // titles grid
      <Grid container direction='column' justify='flex-start' alignItems='flex-start'>
        {this.props.service.getRoot().titles.map(
          (titleObject: IEndpointBuilderTitle) =>
            (titleObject.visible || titleObject.visible === undefined) &&
            titleObject.subtitles.length > 0 && (
              <Grid item>
                {/* title */}
                <p
                  style={{
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    marginBottom: '10px',
                    marginTop: '20px',
                  }}
                >
                  {titleObject.title}&nbsp;&nbsp;
                  {
                    // description
                    titleObject.description && (
                      <Tooltip title={titleObject.description}>
                        <InfoIcon
                          style={{
                            verticalAlign: 'bottom',
                            width: '1rem',
                            color: '#929599',
                            paddingTop: '3px',
                          }}
                        />
                      </Tooltip>
                    )
                  }
                </p>
                {/* render subtitles */}
                {this.renderSubtitle(titleObject)}
              </Grid>
            ),
        )}
      </Grid>
    );

    // render subtitles
    renderSubtitle = (titleObject: IEndpointBuilderTitle) => (
      // subtitles grid
      <Grid container direction='column' justify='flex-start' alignItems='flex-start'>
        {titleObject.subtitles.map(
          (subtitleObject: IEndpointBuilderSubtitle) =>
            (subtitleObject.visible || subtitleObject.visible === undefined) &&
            subtitleObject.chips.length > 0 && (
              <Grid item>
                {!subtitleObject.ignore && (
                  <p style={{ marginBottom: '5px', marginTop: '5px' }}>
                    {subtitleObject.subtitle}&nbsp;&nbsp;
                    {
                      // description
                      subtitleObject.description && (
                        <Tooltip title={subtitleObject.description}>
                          <InfoIcon
                            style={{
                              verticalAlign: 'bottom',
                              width: '1rem',
                              color: '#929599',
                              paddingTop: '3px',
                            }}
                          />
                        </Tooltip>
                      )
                    }
                    &nbsp;&nbsp;
                    {subtitleObject.subtitle === 'Ad Break Definition' && (
                      <a href={getSSAIWiki()} target='_blank' rel='noopener'>
                        Integration Wiki
                      </a>
                    )}
                  </p>
                )}
                {/* render chips */}
                {this.renderChips(subtitleObject)}
              </Grid>
            ),
        )}
      </Grid>
    );

    // render chips
    renderChips = (subtitleObject: IEndpointBuilderSubtitle) => {
      const { classes } = this.props;
      return (
        // chips grid
        <Grid container direction='row' justify='flex-start' alignItems='flex-start' spacing={1}>
          {subtitleObject.chips.map(
            (chipObject: IEndpointBuilderChip) =>
              (chipObject.visible || chipObject.visible === undefined) && (
                <Grid item>
                  <Chip
                    label={chipObject.text}
                    color={chipObject.selected ? 'primary' : 'default'}
                    onClick={() => {
                      this.props.service.handleChipClick(subtitleObject, chipObject);
                    }}
                    style={{ marginBottom: 'unset', marginTop: 'unset' }}
                    disabled={chipObject.active !== undefined ? !chipObject.active : false}
                    className={`${classes.chip} pb-chip-${chipObject.text
                      .toLowerCase()
                      .replace(/\s+/g, '-')
                      .replace(/['"`.]/g, '')}`}
                  />
                </Grid>
              ),
          )}
        </Grid>
      );
    };
  },
);
